import React, {useState} from 'react';

import Image from 'next/image';
import {useRouter} from 'next/router';
import {FormattedMessage} from 'react-intl';
import styled from 'styled-components';

import {colors, dimensions, ActionButton} from '@stryd/react-ui';
import {isNumber, stripeAmountToCurrencyAmount} from '@stryd/util-lib';

import {JULY_2025_EXP} from 'src/components/experiment/experiments';
import {useExperiment} from 'src/components/experiment/useExperiment';
import {regionConfigs} from 'src/config';
import {usePageRegionData} from 'src/contexts';
import {useCart} from 'src/features/cart/contexts';
import {CartTestType} from 'src/features/cart/types';
import {getVariantPricing, VariantPricing} from 'src/features/pricing/helpers';
import {MoneyBag} from 'src/features/pricing/types';
import {
  getStrydContentfulProduct,
  getStrydPlusContentfulProduct,
  getStrydDuoContentfulProduct,
} from 'src/features/product/data';
import {usePriceFormatters} from 'src/hooks/usePriceFormatters';
import {addToCartEvent} from 'src/utils/analytics/events';
import {track} from 'src/utils/analytics/gtag';
import {variantToItems} from 'src/utils/analytics/variantToItems';
import {IProduct} from 'src/utils/contentful';

const columnLayoutBreakpoint = dimensions.breakpoints.tabletLandscape;

const CountdownTimer = styled.img`
  margin: 0 auto;
  padding: 20px 0 0;
  display: block;
`;

const HeroContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding-bottom: 64px;
`;

const HeroTitle = styled.div`
  width: 259px;
  text-align: center;
  font-size: 24px;
  font-weight: 600;
  margin: 45px auto 40px;
`;

const StrydProductCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px 20px;
  padding: 0 24px;

  @media screen and (min-width: ${columnLayoutBreakpoint}) {
    flex-direction: row;
    padding: 0;
  }
`;

const StrydProductCardSpacer = styled.div`
  align-self: stretch;
  border: 1px solid rgba(0, 0, 0, 0.25);
  width: 0px;
`;

const StrydProductCardContainer = styled.div`
  flex: 1;
  position: relative;
  background: #fafafa;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.25);
  border-radius: 12px;
  text-align: center;
  padding: 42px 0 48px;
  overflow: hidden;
  min-width: 320px;
`;

const StrydProductCardBanner = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 35px;
  line-height: 35px;
  font-weight: 600;
  font-size: 20px;
  background: rgb(253, 116, 34);
  color: #fff;
`;

const StrydProductCardBannerSmaller = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 35px;
  line-height: 35px;
  font-weight: 600;
  font-size: 16px;
  background: rgb(253, 116, 34);
  color: #fff;
`;

const StrydProductCardTitle = styled.div`
  position: relative;
  font-weight: 700;
  font-size: 42px;
  line-height: 51px;
  margin-bottom: 27px;
`;

const StrydProductCardTitleBadge = styled.div`
  position: absolute;
  left: 50%;
  transform: translate3d(-50%, 0, 0);
  font-weight: 700;
  font-size: 13px;
  line-height: 16px;
`;

const StrydProductCardDescription = styled.div`
  width: 238px;
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  margin: 27px auto 11px;
  height: 57px;
`;

const StrydProductCardProducts = styled.div`
  display: flex;
  justify-content: center;
  margin: 11px auto 25px;
  column-gap: 24px;
`;

const StrydProductCardMembership = styled.div``;

const StrydProductCardMembershipPlus = styled.div`
  font-size: 10px;
  line-height: 12px;
  text-align: left;
  margin-left: 4px;
`;

const StrydProductCardMembershipTitle = styled.div`
  font-weight: 700;
  font-size: 9px;
  line-height: 12px;
  text-align: left;
  margin-bottom: 4px;

  background: linear-gradient(92.5deg, #ff7b01 29.02%, #dbac00 94.94%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
`;

const StrydProductCardMembershipDescription = styled.div`
  width: 60px;
  font-weight: 600;
  font-size: 8.32301px;
  line-height: 10px;
  color: #fff;
  text-align: left;
`;

const StrydProductCardAction = styled(ActionButton)`
  display: block;
  background: #41a4ff;
  margin: 25px auto 0;
  font-weight: 400;
  font-size: 20px;
  border-radius: 9999px;
`;

const StrydProductCardMembershipNote = styled.div`
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translate3d(-50%, 0, 0);
  font-size: 10px;
  line-height: 15px;
  margin: auto;
  width: 190px;
`;

const HeroMembershipNote = styled.div`
  display: block;
  font-weight: 400;
  font-size: 12px;
  line-height: 15px;
  margin: 22px auto;
  text-align: center;
`;

const HeroFeaturesContainer = styled.div`
  border: 1px solid #000000;
  border-radius: 12px;
  padding: 24px 40px;
  margin: 24px;

  @media screen and (min-width: ${columnLayoutBreakpoint}) {
    margin: 24px auto;
  }
`;

const HeroFeaturesTitle = styled.div`
  font-weight: 400;
  font-size: 24px;
  line-height: 29px;
  margin-bottom: 18px;

  @media screen and (min-width: ${columnLayoutBreakpoint}) {
    max-width: 716px;
  }
`;

const HeroFeaturesContent = styled.div`
  font-weight: 400;
  font-size: 18px;
  line-height: 22px;
`;

const ErrorMessage = styled.p`
  font-size: 0.9rem;
  color: ${colors.themes.dark.error};

  margin: ${dimensions.halfPadding} 0;
`;

interface PodOnlyCardProps {
  podOnlyPricing: VariantPricing;
  maxQtyErr: boolean;
  onClick: () => void;
}

const PodOnlyCard: React.FC<PodOnlyCardProps> = ({
  podOnlyPricing,
  maxQtyErr,
  onClick,
}) => {
  const {locale} = usePageRegionData();
  const {formatOneTimePrice} = usePriceFormatters({
    locale,
  });

  return (
    <StrydProductCardContainer>
      <StrydProductCardTitle>Stryd</StrydProductCardTitle>
      <StrydProductCardDescription>
        <FormattedMessage id="StoreHero.PodOnly.descrition" />
      </StrydProductCardDescription>
      <StrydProductCardProducts>
        <Image src="/images/next-gen-stryd-pod.png" width="110" height="105" />
      </StrydProductCardProducts>
      <div className="flex items-center justify-center gap-4 mb-4">
        <button
          className="bg-blue-500 text-white text-xl px-12 py-4 rounded-full"
          onClick={onClick}
        >
          <FormattedMessage id="actions.pay" />{' '}
          {formatOneTimePrice(podOnlyPricing.oneTimePricesTotal)}
        </button>
      </div>

      {maxQtyErr && (
        <ErrorMessage role="alert" style={{textAlign: 'center'}}>
          <FormattedMessage id="errors.addToCartQuantityError" />
        </ErrorMessage>
      )}
    </StrydProductCardContainer>
  );
};

const PodOnlyCardSixMonths: React.FC<PodOnlyCardProps> = ({
  podOnlyPricing,
  maxQtyErr,
  onClick,
}) => {
  const {locale} = usePageRegionData();
  const {formatOneTimePrice} = usePriceFormatters({
    locale,
  });

  return (
    <StrydProductCardContainer>
      <StrydProductCardBannerSmaller>
        <FormattedMessage id="StoreHero.FreeTrial.cardBanner" />
      </StrydProductCardBannerSmaller>
      <StrydProductCardTitle>Stryd</StrydProductCardTitle>
      <StrydProductCardDescription>
        <FormattedMessage id="StoreHero.PodPlus.descrition" />
      </StrydProductCardDescription>
      <StrydProductCardProducts>
        <Image src="/images/next-gen-stryd-pod.png" width="110" height="105" />
      </StrydProductCardProducts>
      <div className="flex items-center justify-center gap-4 mb-4">
        <button
          className="bg-blue-500 text-white text-xl px-12 py-4 rounded-full"
          onClick={onClick}
        >
          <FormattedMessage id="actions.pay" />{' '}
          {formatOneTimePrice(podOnlyPricing.oneTimePricesTotal)}
        </button>
      </div>
      <StrydProductCardMembershipNote>
        <FormattedMessage id="StoreHero.FreeTrial.cardNote" />
      </StrydProductCardMembershipNote>
      {maxQtyErr && (
        <ErrorMessage role="alert" style={{textAlign: 'center'}}>
          <FormattedMessage id="errors.addToCartQuantityError" />
        </ErrorMessage>
      )}
    </StrydProductCardContainer>
  );
};

interface PodPlusCardProps {
  strydPlusPricing: VariantPricing;
  podOnlyPricing: VariantPricing;
  priceDifference: MoneyBag;
  priceDifferenceSingle: MoneyBag;
  maxQtyErr: boolean;
  onClick: () => void;
}

const PodPlusCard: React.FC<PodPlusCardProps> = ({
  strydPlusPricing,
  podOnlyPricing,
  priceDifference,
  priceDifferenceSingle,
  maxQtyErr,
  onClick,
}) => {
  const {locale} = usePageRegionData();
  const {formatOneTimePrice} = usePriceFormatters({
    locale,
  });

  const recurringPrice = formatOneTimePrice(
    strydPlusPricing.recurringPricesTotals[0]
  );
  const oneTimePricesTotal = formatOneTimePrice(
    strydPlusPricing.oneTimePricesTotal
  );

  return (
    <StrydProductCardContainer>
      <StrydProductCardBanner>
        <FormattedMessage
          id="StoreHero.PodPlus.saving100"
          values={{
            savings: formatOneTimePrice(priceDifferenceSingle, (n) =>
              Math.round(n)
            ),
          }}
        />
      </StrydProductCardBanner>
      <StrydProductCardTitle>
        Stryd
        <StrydProductCardTitleBadge>
          +<FormattedMessage id="membershipName.membership" />
        </StrydProductCardTitleBadge>
      </StrydProductCardTitle>

      <StrydProductCardDescription>
        <FormattedMessage id="StoreHero.PodPlus.descrition" />
      </StrydProductCardDescription>
      <StrydProductCardProducts>
        <Image src="/images/next-gen-stryd-pod.png" width="110" height="105" />
        <StrydProductCardMembership>
          <StrydProductCardMembershipPlus>
            <FormattedMessage id="StoreHero.PodPlus.plus" />
          </StrydProductCardMembershipPlus>
          <div
            style={{
              width: 80,
              height: 80,
              background: `url(/images/membership-card-bg.svg)`,
              backgroundSize: 'cover',
              padding: '10px',
            }}
          >
            <StrydProductCardMembershipTitle>
              <FormattedMessage id="membershipName.membership" />
            </StrydProductCardMembershipTitle>
            <StrydProductCardMembershipDescription>
              <FormattedMessage
                id="StoreHero.PodPlus.commitmentMsg"
                values={{recurringPrice}}
              />
            </StrydProductCardMembershipDescription>
          </div>
        </StrydProductCardMembership>
      </StrydProductCardProducts>
      <div className="flex items-center justify-center gap-4 mb-4">
        <button
          className="bg-blue-500 text-white text-xl px-12 py-4 rounded-full"
          onClick={onClick}
        >
          <FormattedMessage id="actions.pay" /> {oneTimePricesTotal}*
        </button>
        <span className="text-2xl text-gray-500 line-through">
          {formatOneTimePrice(podOnlyPricing.oneTimePricesTotal)}
        </span>
      </div>
      <StrydProductCardMembershipNote>
        <FormattedMessage
          id="StoreHero.PodPlus.note"
          values={{recurringPrice}}
        />
      </StrydProductCardMembershipNote>
      {maxQtyErr && (
        <ErrorMessage role="alert" style={{textAlign: 'center'}}>
          <FormattedMessage id="errors.addToCartQuantityError" />
        </ErrorMessage>
      )}
    </StrydProductCardContainer>
  );
};

interface DuoPlusCardProps {
  strydDuoPricing: VariantPricing;
  podOnlyPricing: VariantPricing;
  priceDifferenceDuo: MoneyBag;
  maxQtyErr: boolean;
  onClick: () => void;
}

const DuoPlusCard: React.FC<DuoPlusCardProps> = ({
  strydDuoPricing,
  podOnlyPricing,
  priceDifferenceDuo,
  maxQtyErr,
  onClick,
}) => {
  const {locale} = usePageRegionData();
  const {formatOneTimePrice} = usePriceFormatters({
    locale,
  });

  const recurringPrice = formatOneTimePrice(
    strydDuoPricing.recurringPricesTotals[0]
  );
  const oneTimePricesTotal = formatOneTimePrice(
    strydDuoPricing.oneTimePricesTotal
  );

  return (
    <StrydProductCardContainer>
      <StrydProductCardBanner>
        <FormattedMessage
          id="StoreHero.Duo.saving199"
          values={{
            savings: formatOneTimePrice(priceDifferenceDuo, (n) =>
              Math.round(n)
            ),
          }}
        />
      </StrydProductCardBanner>
      <StrydProductCardTitle>
        Stryd Duo
        <StrydProductCardTitleBadge>
          +<FormattedMessage id="membershipName.membership" />
        </StrydProductCardTitleBadge>
      </StrydProductCardTitle>

      <StrydProductCardDescription>
        <FormattedMessage id="StoreHero.Duo.descrition" />
      </StrydProductCardDescription>
      <StrydProductCardProducts>
        <Image src="/images/stryd-duo-2.png" width="110" height="110" />
        <StrydProductCardMembership>
          <StrydProductCardMembershipPlus>
            <FormattedMessage id="StoreHero.PodPlus.plus" />
          </StrydProductCardMembershipPlus>
          <div
            style={{
              width: 80,
              height: 80,
              background: `url(/images/membership-card-bg.svg)`,
              backgroundSize: 'cover',
              padding: '10px',
            }}
          >
            <StrydProductCardMembershipTitle>
              <FormattedMessage id="membershipName.membership" />
            </StrydProductCardMembershipTitle>
            <StrydProductCardMembershipDescription>
              <FormattedMessage
                id="StoreHero.Duo.commitmentMsg"
                values={{recurringPrice}}
              />
            </StrydProductCardMembershipDescription>
          </div>
        </StrydProductCardMembership>
      </StrydProductCardProducts>
      <div className="flex items-center justify-center gap-4 mb-4">
        <button
          className="bg-blue-500 text-white text-xl px-12 py-4 rounded-full"
          onClick={onClick}
        >
          <FormattedMessage id="actions.pay" /> {oneTimePricesTotal}*
        </button>
        <span className="text-2xl text-gray-500 line-through">
          {formatOneTimePrice(podOnlyPricing.oneTimePricesTotal, (value) => {
            return value * 2;
          })}
        </span>
      </div>
      <StrydProductCardMembershipNote>
        <FormattedMessage id="StoreHero.Duo.note" values={{recurringPrice}} />
      </StrydProductCardMembershipNote>
      {maxQtyErr && (
        <ErrorMessage role="alert" style={{textAlign: 'center'}}>
          <FormattedMessage id="errors.addToCartQuantityError" />
        </ErrorMessage>
      )}
    </StrydProductCardContainer>
  );
};

interface FreeTrialPodCardProps {
  podOnlyPricing: VariantPricing;
  maxQtyErr: boolean;
  onClick: () => void;
}

const FreeTrialPodCard: React.FC<FreeTrialPodCardProps> = ({
  podOnlyPricing,
  maxQtyErr,
  onClick,
}) => {
  const {locale} = usePageRegionData();
  const {formatOneTimePrice} = usePriceFormatters({
    locale,
  });

  return (
    <StrydProductCardContainer>
      <StrydProductCardBanner>
        <FormattedMessage id="StoreHero.FreeTrial.cardBanner" />
      </StrydProductCardBanner>
      <StrydProductCardTitle>Stryd</StrydProductCardTitle>

      <StrydProductCardDescription>
        <FormattedMessage id="StoreHero.PodOnly.descrition" />
      </StrydProductCardDescription>
      <StrydProductCardProducts>
        <Image src="/images/next-gen-stryd-pod.png" width="110" height="105" />
        <StrydProductCardMembership>
          <StrydProductCardMembershipPlus>
            <FormattedMessage id="StoreHero.PodPlus.plus" />
          </StrydProductCardMembershipPlus>
          <div
            style={{
              width: 80,
              height: 80,
              background: `url(/images/membership-card-bg.svg)`,
              backgroundSize: 'cover',
              padding: '10px',
            }}
          >
            <StrydProductCardMembershipTitle>
              <FormattedMessage id="membershipName.membership" />
            </StrydProductCardMembershipTitle>
            <StrydProductCardMembershipDescription>
              <FormattedMessage id="StoreHero.FreeTrial.cardNote" />
            </StrydProductCardMembershipDescription>
          </div>
        </StrydProductCardMembership>
      </StrydProductCardProducts>
      <StrydProductCardAction onClick={onClick}>
        <FormattedMessage id="actions.pay" />{' '}
        {formatOneTimePrice(podOnlyPricing.oneTimePricesTotal)}
      </StrydProductCardAction>
      {maxQtyErr && (
        <ErrorMessage role="alert" style={{textAlign: 'center'}}>
          <FormattedMessage id="errors.addToCartQuantityError" />
        </ErrorMessage>
      )}
    </StrydProductCardContainer>
  );
};

export const FreeTrial: React.FC = () => {
  const router = useRouter();
  const {locale, region} = usePageRegionData();
  const {currency} = regionConfigs[region];
  const [cart, cartDispatch] = useCart();

  const [maxQtyErr, setMaxQtyErr] = useState(false);

  const strydProduct = getStrydContentfulProduct({locale});
  const strydDuoProduct = getStrydDuoContentfulProduct({locale});

  // Stryd purchase options currently only have one variant
  const podOnlyPricing = getVariantPricing({
    variant: strydProduct.fields.variants[0],
    region,
    currency,
  });

  const strydDuoPricing = getVariantPricing({
    variant: strydDuoProduct.fields.variants[0],
    region,
    currency,
  });

  const handleAddToCart = (product: IProduct) => {
    const variant = product.fields.variants[0];
    const {maxCheckoutQuantity = 10} = variant.fields;

    const itemInCart = cart.lineItems.find(
      (item) => item.selectedVariant.sys.id === variant.sys.id
    );

    if (
      itemInCart &&
      isNumber(maxCheckoutQuantity) &&
      itemInCart.quantity >= maxCheckoutQuantity
    ) {
      setMaxQtyErr(true);
      return;
    }

    setMaxQtyErr(false);

    const isMembershipSelected = product === strydDuoProduct;
    const variantPricing = isMembershipSelected
      ? strydDuoPricing
      : podOnlyPricing;
    const firstRecurringUnitAmount =
      variantPricing.recurringPricesTotals.reduce((total, price) => {
        return total + price.unit_amount;
      }, 0);

    track(
      addToCartEvent({
        ecommerce: {
          currency: variantPricing.oneTimePricesTotal.currency,
          value: stripeAmountToCurrencyAmount(
            variantPricing.oneTimePricesTotal.unit_amount +
              firstRecurringUnitAmount,
            variantPricing.oneTimePricesTotal.currency
          ),
          items: variantToItems(variant),
        },
        includesSubscription: isMembershipSelected,
        includesGift: false,
      })
    );

    /**
     * Clear the cart if the user is adding different product.
     */
    if (!itemInCart) {
      cartDispatch({
        type: 'CLEAR_REGIONAL_CART',
        region,
      });
    }

    if (!isMembershipSelected) {
      cartDispatch({
        type: 'UPDATE_CART_TEST_TYPE',
        testType: CartTestType.FreeTrial,
        region,
      });
    }

    cartDispatch({
      type: 'ADD_QTY_TO_CART',
      region,
      quantity: 1,
      item: {product: product, selectedVariant: variant},
    });

    router.push(`/${region}/${locale}/cart`);
  };

  const handleClickPod = () => {
    handleAddToCart(strydProduct);
  };
  const handleClickDuoPlus = () => {
    handleAddToCart(strydDuoProduct);
  };

  const priceDifferenceDuo: MoneyBag = {
    ...podOnlyPricing.oneTimePricesTotal,
    unit_amount:
      podOnlyPricing.oneTimePricesTotal.unit_amount * 2 -
      strydDuoPricing.oneTimePricesTotal.unit_amount,
  };

  return (
    <HeroContainer>
      <HeroTitle>
        <FormattedMessage id="StoreHero.title" />
      </HeroTitle>
      <StrydProductCards>
        <FreeTrialPodCard
          podOnlyPricing={podOnlyPricing}
          maxQtyErr={maxQtyErr}
          onClick={handleClickPod}
        />
        <StrydProductCardSpacer />
        <DuoPlusCard
          strydDuoPricing={strydDuoPricing}
          podOnlyPricing={podOnlyPricing}
          priceDifferenceDuo={priceDifferenceDuo}
          maxQtyErr={maxQtyErr}
          onClick={handleClickDuoPlus}
        />
      </StrydProductCards>

      <HeroMembershipNote>
        <FormattedMessage id="StoreHero.membershipNotRequired" />
        <br />
        <FormattedMessage id="StoreHero.featuresIntro" />
      </HeroMembershipNote>
      <HeroFeaturesContainer>
        <HeroFeaturesTitle>
          <FormattedMessage id="StoreHero.strydFeaturesTitle" />
        </HeroFeaturesTitle>
        <HeroFeaturesContent>
          <p>
            <FormattedMessage id="StoreHero.strydFeature1" />
          </p>
          <p>
            <FormattedMessage id="StoreHero.strydFeature2" />
          </p>
          <p>
            <FormattedMessage id="StoreHero.strydFeature3" />
          </p>
          <p>
            <FormattedMessage id="StoreHero.strydFeature4" />
          </p>
        </HeroFeaturesContent>
      </HeroFeaturesContainer>
    </HeroContainer>
  );
};
export const Bundles: React.FC<{Variation: string}> = (props) => {
  const router = useRouter();
  const {locale, region} = usePageRegionData();
  const {currency} = regionConfigs[region];
  const [cart, cartDispatch] = useCart();

  const [maxQtyErr, setMaxQtyErr] = useState(false);

  const strydProduct = getStrydContentfulProduct({locale});
  const strydPlusProduct = getStrydPlusContentfulProduct({locale});
  const strydDuoProduct = getStrydDuoContentfulProduct({locale});

  // Stryd purchase options currently only have one variant
  const podOnlyPricing = getVariantPricing({
    variant: strydProduct.fields.variants[0],
    region,
    currency,
  });
  const strydPlusPricing = getVariantPricing({
    variant: strydPlusProduct.fields.variants[0],
    region,
    currency,
  });
  const strydDuoPricing = getVariantPricing({
    variant: strydDuoProduct.fields.variants[0],
    region,
    currency,
  });

  const totalRecurringPrice = strydPlusPricing.recurringPricesTotals.reduce(
    (total, recurring) =>
      total + recurring.min_recurring_intervals * recurring.unit_amount,
    0
  );

  const priceDifference: MoneyBag = {
    ...podOnlyPricing.oneTimePricesTotal,
    unit_amount:
      podOnlyPricing.oneTimePricesTotal.unit_amount -
      strydPlusPricing.oneTimePricesTotal.unit_amount -
      totalRecurringPrice,
  };

  const priceDifferenceSingle: MoneyBag = {
    ...podOnlyPricing.oneTimePricesTotal,
    unit_amount:
      podOnlyPricing.oneTimePricesTotal.unit_amount -
      strydPlusPricing.oneTimePricesTotal.unit_amount,
  };

  const priceDifferenceDuo: MoneyBag = {
    ...podOnlyPricing.oneTimePricesTotal,
    unit_amount:
      podOnlyPricing.oneTimePricesTotal.unit_amount * 2 -
      strydDuoPricing.oneTimePricesTotal.unit_amount,
  };

  const handleAddToCart = (product: IProduct) => {
    const variant = product.fields.variants[0];
    const {maxCheckoutQuantity = 10} = variant.fields;

    const itemInCart = cart.lineItems.find(
      (item) => item.selectedVariant.sys.id === variant.sys.id
    );

    if (
      itemInCart &&
      isNumber(maxCheckoutQuantity) &&
      itemInCart.quantity >= maxCheckoutQuantity
    ) {
      setMaxQtyErr(true);
      return;
    }

    setMaxQtyErr(false);

    const isMembershipSelected =
      product === strydPlusProduct || product === strydDuoProduct;
    const variantPricing = isMembershipSelected
      ? strydPlusPricing
      : podOnlyPricing;
    const firstRecurringUnitAmount =
      variantPricing.recurringPricesTotals.reduce((total, price) => {
        return total + price.unit_amount;
      }, 0);

    track(
      addToCartEvent({
        ecommerce: {
          currency: variantPricing.oneTimePricesTotal.currency,
          value: stripeAmountToCurrencyAmount(
            variantPricing.oneTimePricesTotal.unit_amount +
              firstRecurringUnitAmount,
            variantPricing.oneTimePricesTotal.currency
          ),
          items: variantToItems(variant),
        },
        includesSubscription: isMembershipSelected,
        includesGift: false,
      })
    );

    /**
     * Clear the cart if the user is adding different product.
     */
    if (!itemInCart) {
      cartDispatch({
        type: 'CLEAR_REGIONAL_CART',
        region,
      });
    }

    if (!isMembershipSelected) {
      cartDispatch({
        type: 'UPDATE_CART_TEST_TYPE',
        testType: CartTestType.FreeTrial,
        region,
      });
    }

    cartDispatch({
      type: 'ADD_QTY_TO_CART',
      region,
      quantity: 1,
      item: {product: product, selectedVariant: variant},
    });

    router.push(`/${region}/${locale}/cart`);
  };

  const handleClickPod = () => {
    handleAddToCart(strydProduct);
  };
  const handleClickPodPlus = () => {
    handleAddToCart(strydPlusProduct);
  };
  const handleClickDuoPlus = () => {
    handleAddToCart(strydDuoProduct);
  };

  return (
    <HeroContainer>
      <HeroTitle>
        <FormattedMessage id="StoreHero.title" />
      </HeroTitle>
      <StrydProductCards>
        {props.Variation === 'control' && (
          <PodOnlyCard
            podOnlyPricing={podOnlyPricing}
            maxQtyErr={maxQtyErr}
            onClick={handleClickPod}
          />
        )}
        {props.Variation === 'six-months' && (
          <PodOnlyCardSixMonths
            podOnlyPricing={podOnlyPricing}
            maxQtyErr={maxQtyErr}
            onClick={handleClickPod}
          />
        )}
        <PodPlusCard
          strydPlusPricing={strydPlusPricing}
          podOnlyPricing={podOnlyPricing}
          priceDifference={priceDifference}
          priceDifferenceSingle={priceDifferenceSingle}
          maxQtyErr={maxQtyErr}
          onClick={handleClickPodPlus}
        />
        <StrydProductCardSpacer />
        <DuoPlusCard
          strydDuoPricing={strydDuoPricing}
          podOnlyPricing={podOnlyPricing}
          priceDifferenceDuo={priceDifferenceDuo}
          maxQtyErr={maxQtyErr}
          onClick={handleClickDuoPlus}
        />
      </StrydProductCards>

      <HeroMembershipNote>
        <FormattedMessage id="StoreHero.membershipNotRequired" />
        <br />
        <FormattedMessage id="StoreHero.featuresIntro" />
      </HeroMembershipNote>
      <HeroFeaturesContainer>
        <HeroFeaturesTitle>
          <FormattedMessage id="StoreHero.membershipFeaturesTitle" />
        </HeroFeaturesTitle>
        <HeroFeaturesContent>
          <p>
            <FormattedMessage id="StoreHero.membershipFeature1" />
          </p>
          <p>
            <FormattedMessage id="StoreHero.membershipFeature2" />
          </p>
          <p>
            <FormattedMessage id="StoreHero.membershipFeature3" />
          </p>
          <p>
            <FormattedMessage id="StoreHero.membershipFeature4" />
          </p>
          <p>
            <FormattedMessage id="StoreHero.duoMembershipFeature2" />
          </p>
          <p>
            <FormattedMessage id="StoreHero.duoMembershipFeature3" />
          </p>
        </HeroFeaturesContent>
      </HeroFeaturesContainer>
    </HeroContainer>
  );
};

const storeVariationWeights = [
  {id: 'control', weight: 0.33},
  {id: 'no-pod', weight: 0.33},
  {id: 'six-months', weight: 0.33},
];

export const StoreHero: React.FC = () => {
  const experiment = useExperiment({
    experimentId: JULY_2025_EXP,
    variations: storeVariationWeights,
    enabled: true,
  });

  const storeVariation = experiment.selectedVariant;

  console.log('cv');
  console.log(storeVariation);

  if (storeVariation) {
    return <Bundles Variation={storeVariation.id} />;
  }
  return <Bundles Variation={storeVariationWeights[0].id} />;
};
